// ✅ src/pages/Asset/AssetSubTypeMaster.jsx
// FULL FILE - API INTEGRATED + COLOR PICKER + LOCALSTORAGE HEADERS
//
// Backend mount:
//   app.use("/api/subtypeMaster", assetSubTypeMaster);
//
// Endpoints used:
//   GET  /api/subtypeMaster/assetTypes
//   GET  /api/subtypeMaster/subtypecode
//   GET  /api/subtypeMaster/lastIndex?assetTypeId=
//   GET  /api/subtypeMaster/:id
//   GET  /api/subtypeMaster/transExist/:id
//   POST /api/subtypeMaster/save
//
// Edit mode URL:
//   /assetSubTypeMaster?id=123

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaPlus, FaSave } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/subtypeMaster";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const AssetSubTypeMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const query = useQuery();
  const editId = query.get("id");

  // -------------------------
  // LocalStorage values
  // -------------------------
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers for backend
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId],
  );

  const [loading, setLoading] = useState(false);
  const [assetTypes, setAssetTypes] = useState([
    { value: "", label: "Select" },
  ]);
  const [transExists, setTransExists] = useState(false);

  const [formData, setFormData] = useState({
    assetSubTypeId: 0,
    assetTypeId: "",
    assetSubTypeName: "",
    assetSubTypeCode: "",
    index: "",
    remarks: "",
    colorCode: "",
    active: true,
  });

  const safeSetForm = (patch) => {
    setFormData((p) => ({ ...p, ...patch }));
  };

  // ---- Color helpers ----
const normalizeHex = (v) => {
  if (!v) return "";
  let s = String(v).trim().replace(/\s+/g, "");
  if (!s) return "";

  if (/^[0-9A-Fa-f]{6}$/.test(s)) return `#${s.toUpperCase()}`;
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toUpperCase();

  return s;
};

  const ensureColorInputValue = (v) => {
    const x = normalizeHex(v);
    return /^#[0-9A-Fa-f]{6}$/.test(x) ? x : "#000000";
  };

  // ---- UI classes ----
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const requiredClass = "text-red-500";

  // ---------- API CALLS ----------
  const loadAssetTypes = async () => {
    const r = await axios.get(`${API_BASE}/assetTypes`, {
      headers: commonHeaders,
    });

    const rows = r.data?.data || [];

    const mapped = rows.map((x) => ({
      value:
        x.fld_assettypeid ??
        x.fld_assettypeID ??
        x.assetTypeId ??
        x.AssetTypeId ??
        x.id ??
        x.value ??
        "",
      label:
        x.fld_assettypename ??
        x.assetTypeName ??
        x.AssetType ??
        x.name ??
        x.label ??
        "",
    }));

    setAssetTypes([
      { value: "", label: "Select" },
      ...mapped.filter((m) => m.value !== ""),
    ]);
  };

  const loadNewCode = async () => {
    const r = await axios.get(`${API_BASE}/subtypecode`, {
      headers: commonHeaders,
    });
    const code = r.data?.code || "ASST/1";
    safeSetForm({ assetSubTypeCode: code });
  };

  const loadLastIndex = async (assetTypeId) => {
    if (!assetTypeId) return;
    const r = await axios.get(`${API_BASE}/lastIndex`, {
      headers: commonHeaders,
      params: { assetTypeId },
    });
    const last = r.data?.lastIndex || "";
    safeSetForm({ index: last });
  };

  const loadEdit = async (id) => {
    const r = await axios.get(`${API_BASE}/${id}`, {
      headers: commonHeaders,
    });

    const d = r.data?.data;
    if (!d) return;

    safeSetForm({
      assetSubTypeId: d.assetSubTypeId ?? parseInt(id, 10),
      assetTypeId: d.assetTypeId ?? "",
      assetSubTypeName: d.assetSubTypeName ?? "",
      assetSubTypeCode: d.assetSubTypeCode ?? "",
      index: d.index ?? "",
      remarks: d.remarks ?? "",
      colorCode: d.colorCode ?? "",
      active: !!d.active,
    });

    try {
      const t = await axios.get(`${API_BASE}/transExist/${id}`, {
        headers: commonHeaders,
      });
      const exists = !!t.data?.exists;
      setTransExists(exists);
      if (exists) toast.info("Transaction exists. Active cannot be changed.");
    } catch {
      setTransExists(false);
    }
  };

  // ---------- EFFECTS ----------
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadAssetTypes();

        if (editId) {
          await loadEdit(editId);
        } else {
          await loadNewCode();
        }
      } catch (e) {
        toast.error(
          e?.response?.data?.message || e.message || "Failed to load",
        );
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // ---------- HANDLERS ----------
  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    const nextVal = type === "checkbox" ? checked : value;

    safeSetForm({ [name]: nextVal });

    if (name === "assetTypeId") {
      try {
        await loadLastIndex(nextVal);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load index",
        );
      }
    }
  };

  const validate = () => {
    if (!formData.assetTypeId) return "Asset Type is required!";
    if (!formData.assetSubTypeName?.trim())
      return "Asset Sub Type Name is required!";
    if (!formData.assetSubTypeCode?.trim())
      return "Asset Sub Type Code is required!";
    if (!formData.index?.toString().trim()) return "Index is required!";

    const c = normalizeHex(formData.colorCode);
    if (!/^#[0-9A-Fa-f]{6}$/.test(c)) {
      return "Color is required (valid hex like #AABBCC)";
    }

    return "";
  };

  const handleSave = async () => {
    const msg = validate();
    if (msg) return toast.error(msg);

    try {
      setLoading(true);

      const payload = {
        assetSubTypeId: formData.assetSubTypeId || 0,
        assetTypeId: formData.assetTypeId,
        assetSubTypeCode: formData.assetSubTypeCode.trim(),
        assetSubTypeName: formData.assetSubTypeName.trim(),
        remarks: formData.remarks || "",
        active: !!formData.active,
        colorCode: normalizeHex(formData.colorCode),
        index: formData.index,
      };

      const r = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      toast.success(r.data?.message || "Saved");

      navigate("/Asset/AssetSubTypeMasterListing");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = async () => {
    try {
      setLoading(true);
      setTransExists(false);

      setFormData({
        assetSubTypeId: 0,
        assetTypeId: "",
        assetSubTypeName: "",
        assetSubTypeCode: "",
        index: "",
        remarks: "",
        colorCode: "",
        active: true,
      });

      await loadNewCode();
      toast.info("Form reset for new entry");

      navigate("/Asset/AssetSubTypeMaster");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate("/Asset/AssetSubTypeMasterListing");

  const isEditMode = !!editId;
  const normalizedColor = normalizeHex(formData.colorCode);

  return (
    <AssetLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">ASSET SUB TYPE MASTER</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />
        <button
          onClick={handleBack}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <FaArrowLeft />
          Back
        </button>

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1
                className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {isEditMode ? "Edit Asset Sub Type" : "Asset Sub Type Master"}
              </h1>
              {loading && (
                <span
                  className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
                >
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-end"></div>
          </div>

          <div
            className={`rounded-xl border p-6 ${
              isDark
                ? "border-gray-700 bg-gray-800/50"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Asset Type <span className={requiredClass}>*</span>
                </label>
                <select
                  name="assetTypeId"
                  value={formData.assetTypeId}
                  onChange={handleChange}
                  className={inputClass}
                >
                  {assetTypes.map((t) => (
                    <option key={t.value || t.label} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Asset Sub Type Name <span className={requiredClass}>*</span>
                </label>
                <input
                  type="text"
                  name="assetSubTypeName"
                  value={formData.assetSubTypeName}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter asset sub type name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Asset Sub Type Code <span className={requiredClass}>*</span>
                </label>
                <input
                  type="text"
                  name="assetSubTypeCode"
                  value={formData.assetSubTypeCode}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter asset sub type code"
                  disabled={isEditMode}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Color <span className={requiredClass}>*</span>
                </label>

                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={ensureColorInputValue(formData.colorCode)}
                    onChange={(e) => safeSetForm({ colorCode: e.target.value })}
                    className={`h-10 w-14 rounded-md border ${
                      isDark
                        ? "border-gray-600 bg-gray-700/30"
                        : "border-gray-300 bg-white"
                    }`}
                    title="Pick a color"
                  />

                  <input
                    type="text"
                    name="colorCode"
                    value={formData.colorCode}
                    onChange={(e) =>
                      safeSetForm({ colorCode: normalizeHex(e.target.value) })
                    }
                    className={inputClass}
                    placeholder="#AABBCC"
                  />

                 
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <label className={`${labelClass} text-right md:text-right`}>
                  Index <span className={requiredClass}>*</span>
                </label>
                <input
                  type="text"
                  name="index"
                  value={formData.index}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Enter index"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <label
                  className={`${labelClass} text-right md:text-right pt-2`}
                >
                  Remarks
                </label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  rows={4}
                  className={inputClass}
                  placeholder="Enter remarks"
                />
              </div>

              <div className="border-t pt-6 mt-6 border-gray-200 dark:border-gray-700">
                <div className="flex justify-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active}
                      onChange={handleChange}
                      disabled={transExists}
                      className={`w-4 h-4 text-blue-600 rounded focus:ring-blue-500 ${
                        transExists ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    />
                    <span
                      className={isDark ? "text-gray-300" : "text-gray-700"}
                    >
                      Active
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
               <button
  onClick={handleSave}
  disabled={loading}
  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
    loading
      ? "bg-gray-400 text-white cursor-not-allowed"
      : isDark
        ? "bg-blue-700 hover:bg-blue-600 text-white"
        : "bg-blue-600 hover:bg-blue-700 text-white"
  }`}
>
  <FaSave />
  {isEditMode ? "Update" : "Save"}
</button>

                <button
                  onClick={handleAddNew}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : `${buttonClass} ${hoverClass} text-white`
                  }`}
                >
                  <FaPlus />
                  Add New
                </button>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetSubTypeMaster;
